import React from "react";
import IFieldOptions from "../../../../../../model/interface/form/elementOptions/IFieldOptions";
import FormFieldType from "./FormFieldType";
import LocaleProvider from "../../../../../../i18n/LocaleProvider";
import DatePicker from "components/shared/input/DatePicker";
import TimePicker from "components/shared/input/TimePicker";
import moment, {Moment} from "moment";
import {range} from "underscore";
import IRestResource from "../../../../../../model/interface/api/IRestResource";
import {MomentBuilder} from "../../../../../../utils/MomentBuilder";
import {DisabledTimes} from "rc-picker/lib/interface";

class FormFieldDate {
    public static render = (options: IFieldOptions, resource?: IRestResource) => {
        const props = {
            style: {width: '100%', ...options.style},
            placeholder: options.placeholder,
            allowClear: options.showClear,
            disabled: options.disabled,
            value: options.value,
            format: options.datePickerFormat || '',
            confirm: options.datePickerConfirm
        };

        // 1. Basic Date Validation
        const isDisabledDate = (date: Moment) =>
            (!!options.datePickerAllowedFrom && date.isBefore(moment().subtract(options.datePickerAllowedFrom, 's'), 'day')) ||
            (!!options.datePickerAllowedTo && date.isAfter(moment().add(options.datePickerAllowedTo, 's'), 'day')) ||
            (options.datePickerRelationAfter && resource?.[options.datePickerRelationAfter] && MomentBuilder.build(resource[options.datePickerRelationAfter]).isAfter(date, options.datePicker === FormFieldType.FIELD_DATE_DATE_PICKER ? 's' : 'd')) ||
            (options.datePickerRelationBefore && resource?.[options.datePickerRelationBefore] && MomentBuilder.build(resource[options.datePickerRelationBefore]).isBefore(date, options.datePicker === FormFieldType.FIELD_DATE_DATE_PICKER ? 's' : 'd'));

        // 2. Reusable Time Validator (Simplifies the logic significantly)
        const isInvalidTime = (testDate: Moment, unit: moment.unitOfTime.StartOf) => {
            return (
                isDisabledDate(testDate) ||
                (!!options.datePickerAllowedFrom && testDate.unix() < moment().subtract(options.datePickerAllowedFrom, 's').unix()) ||
                (!!options.datePickerAllowedTo && testDate.unix() > moment().add(options.datePickerAllowedTo, 's').unix()) ||
                (options.datePickerRelationBefore && resource?.[options.datePickerRelationBefore] && MomentBuilder.build(resource[options.datePickerRelationBefore]).isBefore(testDate, unit)) ||
                (options.datePickerRelationAfter && resource?.[options.datePickerRelationAfter] && MomentBuilder.build(resource[options.datePickerRelationAfter]).isAfter(testDate, unit))
            );
        };

        // 3. Logic for DatePicker's showTime prop
        const isDisabledTime = (date: Moment | null): DisabledTimes => {
            const base = date || moment();
            return {
                disabledHours: () => range(0, 24).filter(h => isInvalidTime(base.clone().hour(h), 'h')),
                disabledMinutes: (h) => range(0, 60).filter(m => isInvalidTime(base.clone().hour(h).minute(m), 'm')),
                disabledSeconds: (h, m) => range(0, 60).filter(s => isInvalidTime(base.clone().hour(h).minute(m).second(s), 's')),
            };
        };

        switch (options.datePicker) {
            case FormFieldType.FIELD_DATE_DATE_PICKER:
                return <DatePicker disabledDate={isDisabledDate} locale={LocaleProvider.getPicker()} {...props} />;

            case FormFieldType.FIELD_DATE_DATE_TIME_PICKER:
                return <DatePicker
                    disabledDate={isDisabledDate}
                    disabledTime={isDisabledTime}
                    showTime={{format: options.datePickerTimeFormat}}
                    locale={LocaleProvider.getPicker()}
                    {...props}
                />;

            case FormFieldType.FIELD_DATE_TIME_PICKER:
                const timeBase = options.value ? moment(options.value) : moment();
                return (
                    <TimePicker
                        {...props}
                        locale={LocaleProvider.getPicker()}
                        format={options.datePickerTimeFormat}
                        disabledHours={() => range(0, 24).filter(h => isInvalidTime(timeBase.clone().hour(h), 'h'))}
                        disabledMinutes={(h) => range(0, 60).filter(m => isInvalidTime(timeBase.clone().hour(h).minute(m), 'm'))}
                        disabledSeconds={(h, m) => range(0, 60).filter(s => isInvalidTime(timeBase.clone().hour(h).minute(m).second(s), 's'))}
                    />
                );

            default:
                return <DatePicker disabledDate={isDisabledDate} picker={options.datePicker}
                                   locale={LocaleProvider.getPicker()} {...props} />;
        }
    }
}

export default FormFieldDate;